/**
 * Outil « Bande de concentration » (Boîte à outils, Tle).
 *
 * Pour chaque taille n, 200 moyennes d'échantillons Mₙ ; la bande
 * ]μ − δ ; μ + δ[ posée dessus ; on compte les moyennes qui en sortent et on
 * les compare au majorant de l'inégalité de concentration V/(nδ²). Ce
 * majorant porte sur la PROBABILITÉ P(|Mₙ − μ| ≥ δ) ; il tend vers 0 quand n
 * grandit (loi faible des grands nombres). Logique : `concentration.ts`.
 */

import { useMemo, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { exact } from '../../ui/exact';
import { formatNumber, frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  LAWS,
  SAMPLES,
  SIZES,
  bound,
  boundFrac,
  boundUseless,
  lawById,
  nFor,
  outside,
  outsideCount,
  pct,
  sampleMeans,
  type Law,
  type Size,
} from './concentration';

const W = 340;
const H = 300;
const LEFT = 34;
const TOP = 12;
const BOTTOM = 62;
const PLOT_H = H - TOP - BOTTOM;
const SLOT = (W - LEFT - 6) / SIZES.length;
const IN = 'var(--color-primary-600)';
const OUT = 'var(--color-error-600)';
const MU = 'var(--color-accent-600)';

const num = (v: number, d = 3) => formatNumber(v, d).replace('-', '−');
const group = (n: number) =>
  String(n).replace(/\B(?=(\d{3})+(?!\d))/g, String.fromCharCode(0x202f));
/** décalage horizontal dans la colonne (nombre d'or : pas de points superposés) */
const jitter = (i: number) => (((i * 0.618034) % 1) - 0.5) * SLOT * 0.62;

/** « 25 % », ou « ≥ 1 » quand l'inégalité n'apprend rien */
const boundShort = (law: Law, n: number, delta: number) =>
  boundUseless(law, n, delta) ? '≥ 1' : `≤ ${pct(bound(law, n, delta), true)}`;

function Columns({
  law,
  delta,
  columns,
}: {
  law: Law;
  delta: number;
  columns: Partial<Record<Size, number[]>>;
}) {
  const yOf = (m: number) =>
    TOP + ((law.hi - Math.max(law.lo, Math.min(law.hi, m))) / (law.hi - law.lo)) * PLOT_H;
  const step = law.id === 'die' ? 1 : 0.1;
  const ticks: number[] = [];
  for (let t = law.lo; t <= law.hi + 1e-9; t += step) ticks.push(Number(t.toFixed(6)));
  const lo = law.mean - delta;
  const hi = law.mean + delta;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn(
        'border-ink-100 block w-full max-w-[26rem] select-none rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={`Moyennes des échantillons pour chaque taille n, bande ]μ − δ ; μ + δ[ avec μ = ${law.meanText} et δ = ${num(delta)}.`}
      data-testid="cc-figure"
    >
      {ticks.map((t, i) => (
        <g key={t}>
          <line x1={LEFT} x2={W - 4} y1={yOf(t)} y2={yOf(t)} stroke="var(--color-ink-100)" />
          {(law.id === 'die' || i % 2 === 0) && (
            <text
              x={LEFT - 5}
              y={yOf(t) + 4}
              textAnchor="end"
              fontSize={12}
              fill="var(--color-ink-500)"
            >
              {num(t, 1)}
            </text>
          )}
        </g>
      ))}
      {SIZES.map((n, s) => {
        const cx = LEFT + SLOT * (s + 0.5);
        const ms = columns[n];
        const out = ms ? outsideCount(law, ms, delta) : null;
        const x0 = cx - SLOT * 0.42;
        const x1 = cx + SLOT * 0.42;
        return (
          <g key={n}>
            <rect
              x={x0}
              width={x1 - x0}
              y={yOf(hi)}
              height={Math.max(1, yOf(lo) - yOf(hi))}
              fill="var(--color-success-500)"
              opacity={ms ? 0.22 : 0.08}
            />
            {/* bords de la bande : une moyenne posée dessus compte comme hors de la bande */}
            {[lo, hi].map((y) => (
              <line
                key={y}
                x1={x0}
                x2={x1}
                y1={yOf(y)}
                y2={yOf(y)}
                stroke="var(--color-success-600)"
                strokeDasharray="3 2"
              />
            ))}
            {ms &&
              [false, true].map((wantOut) =>
                ms.map((m, i) => {
                  const o = outside(law, m, delta);
                  if (o !== wantOut) return null;
                  return (
                    <circle
                      key={`${o ? 'o' : 'i'}${i}`}
                      cx={cx + jitter(i)}
                      cy={yOf(m)}
                      r={o ? 3 : 2.2}
                      fill={o ? OUT : IN}
                      stroke={o ? 'var(--fig-bg, #fff)' : 'none'}
                      strokeWidth={1}
                    />
                  );
                }),
              )}
            <text
              x={cx}
              y={H - BOTTOM + 16}
              textAnchor="middle"
              fontSize={12}
              fontWeight={700}
              fill="var(--color-ink-700)"
            >
              {`n = ${group(n)}`}
            </text>
            <text
              x={cx}
              y={H - BOTTOM + 32}
              textAnchor="middle"
              fontSize={12}
              fontWeight={out ? 700 : 400}
              fill={out ? OUT : 'var(--color-ink-500)'}
            >
              {out === null ? '' : `${out}/${SAMPLES}`}
            </text>
            <text
              x={cx}
              y={H - BOTTOM + 48}
              textAnchor="middle"
              fontSize={12}
              fill="var(--color-ink-500)"
            >
              {boundShort(law, n, delta)}
            </text>
          </g>
        );
      })}
      <line
        x1={LEFT}
        x2={W - 4}
        y1={yOf(law.mean)}
        y2={yOf(law.mean)}
        stroke={MU}
        strokeWidth={1.5}
        strokeDasharray="6 4"
      />
      <HaloText
        x={W - 8}
        y={yOf(law.mean) - 5}
        textAnchor="end"
        fontSize={12}
        fontWeight={700}
        fill={MU}
      >
        μ
      </HaloText>
    </svg>
  );
}

export function ConcentrationTool({ seed }: { /** graine des tirages (tests) */ seed?: number }) {
  const rng = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const [lid, setLid] = useState(LAWS[0]!.id);
  const law = lawById(lid);
  const [di, setDi] = useState(1);
  const delta = law.deltas[di] ?? law.deltas[0]!;
  const [n, setN] = useState<Size>(100);
  const [columns, setColumns] = useState<Partial<Record<Size, number[]>>>({});
  const [last, setLast] = useState<Size | null>(null);
  const drawn = SIZES.filter((m) => columns[m]);
  const bandText = `]${num(law.mean - delta)} ; ${num(law.mean + delta)}[`;

  const lastText = () => {
    if (!last || !columns[last]) return 'Choisis n, puis tire des échantillons.';
    const out = outsideCount(law, columns[last]!, delta);
    const b = boundFrac(law, last, delta);
    return boundUseless(law, last, delta)
      ? `n = ${group(last)} : ${out} moyennes sur ${SAMPLES} hors de la bande ; V/(nδ²) = ${exact(b).plain} ≥ 1 : ici l’inégalité n’apprend rien.`
      : `n = ${group(last)} : ${out} moyennes sur ${SAMPLES} hors de la bande (${pct(out / SAMPLES)}) ; majorant V/(nδ²) = ${exact(b).plain} ≈ ${pct(bound(law, last, delta), true)}.`;
  };

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Variable X" className="flex flex-wrap gap-1.5">
        {LAWS.map((l) => (
          <ToggleChip
            key={l.id}
            on={l.id === lid}
            onClick={() => {
              setLid(l.id);
              setColumns({});
              setLast(null);
            }}
          >
            {l.label}
          </ToggleChip>
        ))}
      </div>
      <p className="text-ink-700 text-sm">
        {frenchSpaces(
          `${law.text}. μ = E(X) = ${law.meanText}, V = V(X) = ${law.varText}. Mₙ = (X₁ + ⋯ + Xₙ)/n, où X₁, …, Xₙ sont indépendantes et de même loi que X (échantillon de taille n).`,
        )}
      </p>
      <div className="space-y-1">
        <p className="text-ink-500 text-xs font-semibold">Demi-largeur δ de la bande</p>
        <Segmented
          ariaLabel="Demi-largeur δ de la bande"
          value={String(di)}
          options={law.deltas.map((d, i) => ({ value: String(i), label: num(d) }))}
          onChange={(v) => setDi(Number(v))}
        />
      </div>
      <div className="space-y-1">
        <p className="text-ink-500 text-xs font-semibold">Taille n des échantillons</p>
        <Segmented
          ariaLabel="Taille n des échantillons"
          value={String(n)}
          options={SIZES.map((m) => ({ value: String(m), label: group(m) }))}
          onChange={(v) => setN(Number(v) as Size)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={PRIMARY_BUTTON}
          onClick={() => {
            setColumns((c) => ({ ...c, [n]: sampleMeans(law, n, rng) }));
            setLast(n);
          }}
        >
          {frenchSpaces(`Tirer ${SAMPLES} échantillons de taille ${group(n)}`)}
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          onClick={() => {
            setColumns({});
            setLast(null);
          }}
          disabled={!drawn.length}
        >
          Effacer
        </button>
      </div>
      <Columns law={law} delta={delta} columns={columns} />
      <p className="text-ink-600 text-xs">
        {frenchSpaces(
          `Chaque point est une moyenne Mₙ ; la bande verte est ]μ − δ ; μ + δ[ = ${bandText}, la ligne pointillée μ. Une moyenne sur le bord (|Mₙ − μ| = δ) compte comme hors de la bande. Sous chaque colonne : les moyennes hors de la bande, puis le majorant V/(nδ²) de l’inégalité de concentration. Ce majorant porte sur la probabilité P(|Mₙ − μ| ≥ δ), pas sur la fréquence observée ; 0 sur 200 ne veut pas dire probabilité nulle.`,
        )}
      </p>
      <p className="text-ink-800 text-sm" aria-live="polite" data-testid="cc-summary">
        {frenchSpaces(lastText())}
      </p>
      <Note live={false}>
        {frenchSpaces(
          `P(|Mₙ − μ| ≥ δ) ≤ V/(nδ²). Avec δ = ${num(delta)}, ce majorant vaut au plus 5 % dès n ≥ ${group(nFor(law, delta, 0.05))}. Il tend vers 0 quand n → +∞ : pour tout δ > 0, P(|Mₙ − μ| ≥ δ) → 0. C’est la loi faible des grands nombres : un grand échantillon a une forte probabilité d’avoir une moyenne proche de μ.`,
        )}
      </Note>
    </div>
  );
}
